/**
 * ChatMessageList.tsx
 *
 * Scrollable message list component with date grouping and infinite scroll.
 * Extracted from ChatInner.v2.tsx.
 */

import React, { FC, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import moment from "moment"
import Message from "src/app/models/Message"
import { Collaboration } from "src/app/models/Chat"
import { ChatMessage } from "src/app/modules/chat/components"

export interface ChatMessageListProps {
  messages: Message[]
  collaboration: Collaboration | undefined
  isLoading: boolean
  hasMore: boolean
  debug: boolean
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void
  showScrollBottom: boolean
  onScrollToBottom?: () => void
  /** If true, auto-scroll to bottom when new messages arrive */
  autoScrollOnNewMessage?: boolean
}

const ChatMessageList: FC<ChatMessageListProps> = ({
  messages,
  collaboration,
  isLoading,
  hasMore,
  debug,
  onScroll,
  showScrollBottom,
  onScrollToBottom,
  autoScrollOnNewMessage = true,
}) => {
  const { t } = useTranslation()
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" })
  const chatBodyRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef<number>(messages.length)
  const isInitialLoadRef = useRef<boolean>(true)
  const previousScrollHeightRef = useRef<number>(0)
  const previousScrollTopRef = useRef<number>(0)
  const previousMessageCountRef = useRef<number>(0)

  // Scroll to bottom helper - use local ref for reliability
  const scrollToBottom = useCallback((smooth = true) => {
    if (!chatBodyRef.current) return

    requestAnimationFrame(() => {
      if (!chatBodyRef.current) return
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "instant",
      })
    })
  }, [])

  // Handle scroll to bottom button click
  const handleScrollToBottom = useCallback(() => {
    scrollToBottom(true)
    // Also call parent callback if provided
    onScrollToBottom?.()
  }, [scrollToBottom, onScrollToBottom])

  // Wrap onScroll to capture scroll position in real-time
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    // Capture scroll state on every scroll for accurate restoration later
    if (chatBodyRef.current) {
      previousScrollTopRef.current = chatBodyRef.current.scrollTop
      previousScrollHeightRef.current = chatBodyRef.current.scrollHeight
    }
    // Call parent handler
    onScroll(event)
  }, [onScroll])

  // Memoized rendered messages with date grouping
  const renderedMessages = useMemo(() => {
    const DAY_IN_MS = 86400000
    const todayDate = new Date()
    const today = todayDate.getTime()

    const filteredMessages = messages
      .slice()
      .filter(
        (msg) =>
          msg.textContent ||
          msg.mediaUrl ||
          msg.product ||
          msg.order ||
          msg.orderSN ||
          msg.location ||
          msg.messageType === "unsupported"
      );

    return filteredMessages.reduce(
        (acc: { dates: Set<string>; uniqueMessages: React.ReactNode[] }, message: Message, index) => {
          const messageDate = new Date(message?.createdAt?.seconds * 1000)
          let formattedDate = moment(messageDate).format("D MMMM YYYY")
          const isThisWeek =
            Math.abs(today - messageDate.getTime()) <= 6 * DAY_IN_MS

          if (moment(messageDate).isSame(moment(), "day")) {
            formattedDate = "T"
          } else if (
            moment(messageDate).isSame(moment().subtract(1, "day"), "day")
          ) {
            formattedDate = "Y"
          } else if (isThisWeek) {
            formattedDate = moment(messageDate).format("dddd")
          }

          const messageKey = `${formattedDate}-${message.id}`

          if (!acc.dates.has(formattedDate)) {
            acc.dates.add(formattedDate)
            acc.uniqueMessages.push(
              <div key={`group-${messageKey}`}>
                <div className="d-flex flex-row justify-content-center align-items-center position-sticky top-0 py-0 mt-0 h-auto bg-white">
                  <p className="text-center bg-secondary px-3 py-2 rounded-pill">
                    {formattedDate === "T"
                      ? t("Date.Today")
                      : formattedDate === "Y"
                      ? t("Date.Yesterday")
                      : formattedDate}
                  </p>
                </div>
                <ChatMessage
                  key={message.id}
                  message={message}
                  index={index}
                  customer={collaboration?.customerModel}
                  lastUserInteraction={collaboration?.lastUserInteractionAt}
                  user={message?.userModel}
                  messages={messages}
                  collaborationId={collaboration?.id ?? ""}
                  companyID={String(collaboration?.company?.id)}
                  phoneNumber={collaboration?.phoneNumber || ""}
                  debug={debug}
                />
              </div>
            )
          } else if (
            message?.source === "system" ||
            message?.messageType === "system_message"
          ) {
            acc.uniqueMessages.push(
              <div
                key={`system-${message.id}`}
                className="d-flex flex-row justify-content-center align-items-center py-0 mt-0 h-auto bg-white"
              >
                <p className="text-center bg-secondary px-3 py-2 rounded-pill">
                  {message?.textContent}
                </p>
              </div>
            )
          } else {
            acc.uniqueMessages.push(
              <ChatMessage
                key={message.id}
                message={message}
                index={index}
                customer={collaboration?.customerModel}
                lastUserInteraction={collaboration?.lastUserInteractionAt}
                user={message?.userModel}
                messages={messages}
                collaborationId={collaboration?.id ?? ""}
                companyID={String(collaboration?.company?.id)}
                phoneNumber={collaboration?.phoneNumber || ""}
                debug={debug}
              />
            )
          }

          return acc
        },
        { dates: new Set<string>(), uniqueMessages: [] as React.ReactNode[] }
      ).uniqueMessages
  }, [messages, collaboration, debug, t])

  // Reset initial load state when switching chats
  useEffect(() => {
    isInitialLoadRef.current = true
    prevMessagesLengthRef.current = 0
    previousMessageCountRef.current = 0
    previousScrollHeightRef.current = 0
    previousScrollTopRef.current = 0
  }, [collaboration?.id])

  // CRITICAL: Use useLayoutEffect for scroll restoration
  // This runs synchronously after DOM mutations but BEFORE browser paint
  // Scroll position is captured in real-time by handleScroll
  useLayoutEffect(() => {
    if (!chatBodyRef.current) return

    const currentMessageCount = renderedMessages.length
    const newScrollHeight = chatBodyRef.current.scrollHeight

    // Detect if older messages were prepended (not initial load, not room switch)
    if (
      previousMessageCountRef.current > 0 &&
      currentMessageCount > previousMessageCountRef.current &&
      previousScrollHeightRef.current > 0
    ) {
      // Messages were prepended - restore scroll position immediately
      const heightAdded = newScrollHeight - previousScrollHeightRef.current
      if (heightAdded > 0) {
        const newScrollTop = previousScrollTopRef.current + heightAdded
        chatBodyRef.current.scrollTop = newScrollTop
        // Update refs to reflect new position after restoration
        previousScrollTopRef.current = newScrollTop
        previousScrollHeightRef.current = newScrollHeight
      }
    }

    // Update message count for next comparison
    previousMessageCountRef.current = currentMessageCount
  }, [renderedMessages.length])

  // Auto-scroll to bottom for initial load and new incoming messages
  useEffect(() => {
    if (!autoScrollOnNewMessage || !chatBodyRef.current) return

    // On initial load, scroll to bottom immediately
    if (isInitialLoadRef.current && renderedMessages.length > 0 && !isLoading) {
      scrollToBottom(false)
      isInitialLoadRef.current = false
      prevMessagesLengthRef.current = renderedMessages.length
      return
    }

    // For new messages (not older messages), auto-scroll if user is near bottom
    // Skip if message count increased by more than a few (likely pagination)
    const prevLength = prevMessagesLengthRef.current
    const currentLength = renderedMessages.length
    const messageIncrease = currentLength - prevLength

    // If more than 5 messages added at once, it's likely pagination - don't auto-scroll
    if (messageIncrease > 5) {
      prevMessagesLengthRef.current = currentLength
      return
    }

    // Only auto-scroll if user is near bottom (within 100px)
    if (currentLength > prevLength && prevLength > 0) {
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100
      if (isNearBottom) {
        scrollToBottom(true)
      }
    }

    prevMessagesLengthRef.current = currentLength
  }, [renderedMessages.length, isLoading, autoScrollOnNewMessage, scrollToBottom])

  return (
    <div
      id="chat-body"
      ref={chatBodyRef}
      className="d-flex flex-column w-100"
      style={{
        flex: "1 1 0", // Grow, shrink, basis 0 - fills available space
        minHeight: 0, // Critical for flex scroll to work
        overflowX: "hidden",
        overflowY: "auto", // auto instead of scroll - shows scrollbar only when needed
        overflowAnchor: "auto", // Enable browser scroll anchoring
      }}
      onScroll={handleScroll}
    >
      {/* Scroll to bottom button */}
      {showScrollBottom && (
        <div
          className="d-flex position-absolute rounded-circle p-3 bg-primary cursor-pointer"
          style={{
            right: isTabletOrMobile ? 5 : 2,
            bottom: isTabletOrMobile ? "12%" : "15%",
            zIndex: 200,
          }}
          onClick={handleScrollToBottom}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            className="text-light-primary"
            transform="scale(1, -1)"
            style={{ transformOrigin: "center" }}
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              d="M9 19v-7H5.483a.2.2 0 0 1-.142-.341L12 5l6.659 6.659a.2.2 0 0 1-.142.341H15v7"
            />
          </svg>
        </div>
      )}

      {/* Loading indicator at top when fetching older messages */}
      {isLoading && (
        <div className="d-flex justify-content-center p-3" style={{ overflowAnchor: "none" }}>
          <span className="spinner-border spinner-border-sm align-middle ms-2" />
        </div>
      )}

      {/* Messages - always rendered to maintain scroll position */}
      {renderedMessages}
    </div>
  )
}

export default ChatMessageList
